import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  FlaskConical, 
  Package, 
  Palette, 
  Factory,
  Zap,
  Droplets,
  Wine,
  Beer,
  Coffee,
  Leaf,
  Dumbbell,
  Sparkles,
  CheckCircle2,
  ChevronRight
} from "lucide-react";
import Footer from "@/components/Footer";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1767709122885-99997d612f76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsYWJvcmF0b3J5JTIwZmxhc2slMjBnbG93aW5nJTIwZ3JlZW4lMjBsaXF1aWQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzE5MTc1ODR8MA&ixlib=rb-4.1.0&q=85",
  scientist: "https://images.pexels.com/photos/15831822/pexels-photo-15831822.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  factory: "https://images.pexels.com/photos/5532836/pexels-photo-5532836.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bottling: "https://images.unsplash.com/photo-1530037335614-e68828dcf258?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxib3R0bGluZyUyMGxpbmUlMjBiZXZlcmFnZSUyMGZhY3RvcnklMjBwcm9kdWN0aW9ufGVufDB8fHx8MTc3MTk5NDg1NXww&ixlib=rb-4.1.0&q=85",
};

const categories = [
  { name: "Refrescos", icon: Droplets, color: "text-blue-400" },
  { name: "Energéticas", icon: Zap, color: "text-yellow-400" },
  { name: "Aguas Funcionales", icon: Sparkles, color: "text-cyan-400" },
  { name: "Kombucha", icon: Leaf, color: "text-green-400" },
  { name: "Cervezas", icon: Beer, color: "text-amber-400" },
  { name: "Vinos", icon: Wine, color: "text-red-400" },
  { name: "Licores", icon: FlaskConical, color: "text-purple-400" },
  { name: "RTD", icon: Package, color: "text-pink-400" },
  { name: "Proteicas", icon: Dumbbell, color: "text-orange-400" },
  { name: "Café & Té", icon: Coffee, color: "text-amber-600" },
];

const steps = [
  {
    number: "01",
    title: "Diseña tu bebida",
    description: "Selecciona categoría, perfil de sabor e ingredientes activos.",
    icon: FlaskConical,
  },
  {
    number: "02",
    title: "Selecciona envase",
    description: "Elige entre vidrio, PET, lata o bag in box con opciones de cierre.",
    icon: Package,
  },
  {
    number: "03",
    title: "Desarrolla tu marca",
    description: "Genera nombre con IA o aporta tu identidad visual completa.",
    icon: Palette,
  },
  {
    number: "04",
    title: "Recibe dossier técnico",
    description: "Nuestro laboratorio valida y nuestra fábrica produce.",
    icon: Factory,
  },
];

const features = [
  "Instalaciones industriales propias",
  "Laboratorio de formulación I+D",
  "Líneas de embotellado automatizadas",
  "Control de calidad interno",
  "Capacidad de producción escalable",
  "Gestión regulatoria incluida",
];

export default function HomePage() {
  return (
    <main className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.hero} 
            alt="Laboratory" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-udl-bg via-udl-bg/90 to-transparent" />
          <div className="absolute inset-0 grid-pattern" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 md:pt-32 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-udl-primary/10 border border-udl-primary/30 mb-6 md:mb-8">
              <span className="w-2 h-2 bg-udl-primary rounded-full animate-pulse" />
              <span className="text-udl-primary text-xs font-mono uppercase tracking-wider">
                Beverage Copacker
              </span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-udl-white leading-tight mb-4 md:mb-6">
              Crea tu propia bebida.{" "}
              <span className="text-udl-primary glow-text">Nosotros la fabricamos.</span>
            </h1>
            
            <p className="text-udl-gray-400 text-base md:text-lg lg:text-xl leading-relaxed mb-8 md:mb-10 max-w-2xl">
              Desarrolla, valida y produce tu bebida con infraestructura industrial real. 
              Del laboratorio a la línea de producción, todo en nuestras instalaciones.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link to="/configurador" className="btn-primary flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                Desarrollar mi bebida
                <ArrowRight size={18} />
              </Link>
              <Link to="/fabrica" className="btn-secondary flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
                Conocer nuestra fábrica
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator - hidden on mobile */}
        <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-udl-gray-700">
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-udl-gray-700 to-transparent" />
        </div>
      </section>

      {/* Powerful Message Section */}
      <section className="py-12 md:py-20 bg-udl-primary" data-testid="message-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <p className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black leading-tight">
            ¿Siempre has querido crear y tener tu propia bebida con tu propia marca y comercializarla?{" "}
            <span className="block mt-2">En Unique Drinks Lab lo hacemos posible.</span>
          </p>
        </div>
      </section>

      {/* What is UDL */}
      <section className="py-16 md:py-32 bg-udl-surface relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
                Qué es Unique Drinks Lab
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white mb-6 md:mb-8">
                Fábrica + Laboratorio = <span className="text-udl-primary">Tu bebida</span>
              </h2>
              <p className="text-udl-gray-400 text-base md:text-lg leading-relaxed mb-6 md:mb-8">
                Unique Drinks Lab es una fábrica de bebidas con laboratorio propio que combina 
                innovación científica e infraestructura industrial para desarrollar bebidas 
                personalizadas listas para producción real.
              </p>
              <div className="space-y-3 md:space-y-4">
                {["No externalizamos.", "No intermediamos.", "Fabricamos."].map((text, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-udl-primary flex-shrink-0" size={20} />
                    <span className="text-udl-white font-medium">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative order-first md:order-last">
              <div className="aspect-square bg-udl-card border border-udl-gray-800 overflow-hidden">
                <img 
                  src={IMAGES.scientist} 
                  alt="Scientist in lab with protective gear" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-udl-primary p-4 md:p-6">
                <p className="text-black font-heading font-bold text-xl md:text-2xl">100%</p>
                <p className="text-black/80 text-xs md:text-sm">Producción propia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-32 relative" data-testid="process-section">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-20">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
              Proceso
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white">
              Cómo funciona
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="card-feature group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-4 md:mb-6">
                  <span className="text-udl-primary font-mono text-3xl md:text-4xl font-bold opacity-30 group-hover:opacity-100 transition-opacity duration-500">
                    {step.number}
                  </span>
                  <step.icon className="text-udl-gray-700 group-hover:text-udl-primary transition-colors duration-500" size={28} />
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-udl-white mb-2 md:mb-3">
                  {step.title}
                </h3>
                <p className="text-udl-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-32 bg-udl-surface" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-20">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
              Categorías
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white">
              Qué puedes crear
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {categories.map((cat, index) => (
              <Link
                key={cat.name}
                to="/configurador"
                className="group bg-udl-card border border-udl-gray-800 p-4 md:p-6 hover:border-udl-primary/50 transition-colors duration-300"
              >
                <cat.icon className={`${cat.color} mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`} size={24} />
                <p className="text-udl-white font-medium text-xs md:text-sm">{cat.name}</p>
              </Link>
            ))}
          </div>

          <div className="mt-6 md:mt-8 text-center">
            <Link to="/configurador" className="inline-flex items-center gap-2 text-udl-primary hover:text-udl-primary-hover transition-colors">
              <Sparkles size={18} />
              <span className="text-sm font-medium">+ Fórmula totalmente personalizada</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-16 md:py-32 relative overflow-hidden" data-testid="infrastructure-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.factory} 
            alt="Factory" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-udl-bg via-udl-bg/80 to-udl-bg" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-3 md:mb-4 block">
                Infraestructura
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white mb-6 md:mb-8">
                Controlamos cada fase del proceso
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 bg-udl-bg/50 backdrop-blur-sm border border-udl-gray-800 p-3 md:p-4">
                    <div className="w-2 h-2 bg-udl-primary flex-shrink-0" />
                    <span className="text-udl-gray-400 text-xs md:text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video bg-udl-card border border-udl-gray-800 overflow-hidden">
                <img 
                  src={IMAGES.bottling} 
                  alt="Bottling line" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-32 bg-udl-surface relative" data-testid="cta-section">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-udl-white mb-4 md:mb-6">
            Empieza a desarrollar tu bebida hoy
          </h2>
          <p className="text-udl-gray-400 text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">
            Configura tu bebida en minutos y recibe un dossier técnico profesional 
            para iniciar la producción.
          </p>
          <Link to="/configurador" className="btn-primary inline-flex items-center gap-2" data-testid="final-cta">
            Comenzar ahora
            <ArrowRight size={18} />
          </Link>
          <p className="mt-6 md:mt-8 text-udl-primary font-heading font-bold text-xs md:text-sm tracking-widest">
            FROM FORMULA TO FACTORY
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
